/**
 * 本地组件调试模块
 *
 * 提供了本地组件调试的功能
 *
 */
import Vue from 'vue';
import MagicUi from '@/ui';
import { getUrlParam, asyncLoadJs } from '@tencent/magic-ui-util';
import { registerSvelteComponentSync } from '@/svelte-comp-adapter';

export const isDevComp = () => getUrlParam('dev-comp') === 'on';

/**
 * 组件调试配置本地缓存key
 */
const DEV_COMP_CONFIG_LS_KEY = 'dev_comp_config';

/**
 * 组件调试js地址
 *
 * https://git.woa.com/ovb-act/comps-debugger.git
 */
const DEV_WEB_COMP_DEBUGGER_URL = `//${location.host.replace('-test', '')}/dev-comp/act-web-debugger.iife.js`;

/**
 * 调试器加载完之后的事件名
 */
const DEV_DEBUGGER_EVENT = 'debuggerLoadedEvent';

function logger(...args: any[]) {
  console.log('[dev-comp]', ...args);
}

function logError(...args: any[]) {
  console.error('[dev-comp]', ...args);
  if (Vue.prototype.$message) {
    Vue.prototype.$message?.({
      type: 'error',
      duration: 10000,
      message: `本地组件调试失败: ${args.join(';')}`,
    });
  }
}

/**
 * 异步import
 *
 * @param importUrl 需要import的模块链接
 *
 * @returns 被加载的模块
 */
async function dynamicImportMod(importUrl: string) {
  try {
    // 在一些旧版本的浏览器上（例如：腾讯视频PC客户端 webview, chromium 43），
    // 不支持异步import，会导致报语法错误，从而使得整个页面报错，
    // 因此使用动态函数的方式来实现加载
    // eslint-disable-next-line
    const func = new Function(`return import( /* @vite-ignore */ /* webpackIgnore: true */ '${importUrl}')`);
    return await func();
  } catch (e) {
    logError('导入模块失败, message: ', (e as Error).message, ', stack: ', (e as Error).stack);
    return null;
  }
}

/**
 * 根据组件类型在全局uiConfig中找到对应的ui配置
 *
 * @param compType 组件类型
 *
 * @returns 组件ui配置列表
 */
function findAllUiConfigsByCompType(compType: string) {
  const result: any = [];
  const root = window.magicUiconfig?.[0].items;
  const track = (list: any) => {
    list.forEach((comp: any) => {
      if (comp.type === compType) {
        result.push(comp);
      }
      if (comp.items) {
        track(comp.items);
      }
    });
  };
  track(root);
  return result;
}

function whenDebuggerLoaded() {
  return new Promise((resolve) => {
    document.body.addEventListener(DEV_DEBUGGER_EVENT, resolve);
  });
}

// 标记组件高亮
function setDevCompHighLight(devCompUiConfigParam: any) {
  const devCompUiConfig = devCompUiConfigParam;
  const compHighLightStyle = {
    // 高亮该组件，使用 outline 不影响组件尺寸，不影响布局
    outline: '2px solid #4ff456',
  };
  if (devCompUiConfig.style) {
    devCompUiConfig.style = {
      ...devCompUiConfig.style,
      ...compHighLightStyle,
    };
    logger('uiConfig', JSON.stringify(devCompUiConfig, null, 2));
    return;
  }
  devCompUiConfig.style = compHighLightStyle;
  logger('uiConfig', JSON.stringify(devCompUiConfig, null, 2));
};

/**
 * 注册本地开发中的组件到C端页面中
 *
 * @param MagicUiInstance MagicUi 实例
 * @returns 本地组件名字列表
 */
export const registerDevComp = async (MagicUiInstance: typeof MagicUi) => {
  await asyncLoadJs(DEV_WEB_COMP_DEBUGGER_URL);

  await whenDebuggerLoaded();

  const localComps: Array<string> = [];
  const localPlugins: Array<string> = [];
  let debugDevCompConfig: any = localStorage.getItem(DEV_COMP_CONFIG_LS_KEY);

  if (!debugDevCompConfig) {
    logError('解析本地调试配置失败, 请联系中台开发同学');
    return { localComps, localPlugins };
  }

  if (debugDevCompConfig) {
    try {
      debugDevCompConfig = JSON.parse(debugDevCompConfig);
    } catch (e) {
      logError('解析本地调试配置失败, 请联系中台开发同学');
      return { localComps, localPlugins };
    }
  }

  const devCompUrl = debugDevCompConfig.url;
  const { isHighLightDevComp } = debugDevCompConfig;
  const devCompNames = debugDevCompConfig.names ? debugDevCompConfig.names.split(',') : [];

  if (!devCompUrl) {
    return { localComps, localPlugins };
  }

  try {
    const importResult = await dynamicImportMod(devCompUrl);

    if (!importResult) {
      logError(`异步加载模块失败: ${devCompUrl}`);
      return { localComps, localPlugins };
    }

    logger('所有组件: ', importResult.devComponents);

    let devComponents;

    if (devCompNames.length === 0) {
      devComponents = importResult.devComponents;
    } else {
      devComponents = importResult.devComponents.filter((item: any) => devCompNames.indexOf(item.compType) > -1);
    }

    devComponents.forEach((devComponent: any) => {
      const {
        default: devComp,
        value: devCompInitValue,
        compType: devCompType,
        name,
      } = devComponent;

      logger('devComp: ', devComp);
      logger('devCompInitValue: ', devCompInitValue);
      logger('devCompType: ', devCompType);
      logger('name: ', name);

      const allTypedUiConfigList = findAllUiConfigsByCompType(devCompType);

      if (allTypedUiConfigList.length > 0 && isHighLightDevComp) {
        allTypedUiConfigList.forEach((uiConfig: any) => {
          setDevCompHighLight(uiConfig);
        });
      } else {
        if (isHighLightDevComp) {
          setDevCompHighLight(devCompInitValue);
        }

        // 补充 type
        devCompInitValue.type = devCompInitValue.type || devCompType;

        // 在活动配置中没有本地开发的组件信息，
        // 直接读取本地的组件初始值，将其合并到已有的活动的配置数据中
        // 默认是合并到一级（好像没办法递归？）
        if (debugDevCompConfig.shouldRenderDevCompsByDefault) {
          window.magicUiconfig?.[0].items[0].items.unshift(devCompInitValue);
        }
      }

      if (/svelte/.test(name)) {
        logger(`svelte 注册组件: ${name}`);
        registerSvelteComponentSync(name, devComp, true /** 覆盖已有组件 */);
      } else {
        // 注册本地组件 hook
        if (MagicUiInstance.hooksMixin) {
          MagicUiInstance.hooksMixin(devComp);
        }

        // 注册本地组件
        let localCompName = `magic-ui-${devCompType.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        if (devCompType === 'app') {
          localCompName = 'magic-ui';
        }
        localComps.push(localCompName);
        logger(`vue 注册组件: ${localCompName}`);
        Vue.component(localCompName, devComp);
      }
    });

    if (importResult.devPlugins) {
      // 插件处理
      importResult.devPlugins.forEach((devPlugin: any) => {
        localPlugins.push(devPlugin);
        Vue.use(devPlugin);
      });
    }
  } catch (e) {
    logError('[dev-comp] get dev comp error:', e);
  }

  // 返回值应该暂时没有用到
  return { localComps, localPlugins };
};
